import * as React from 'react';
import { Icon } from '../icons.jsx';
import { Badge, Button, Field, Input } from '../ui.jsx';

const statusTone = {
  pending: 'wait',
  approved: 'open',
  rejected: 'coral',
  withdrawn: 'neutral',
};

export const ProviderAccessLoading = ({ children }) => (
  <div className="provider-access-state">
    <span className="live-dot" aria-hidden="true"/>
    {children}
  </div>
);

export const ProviderAccessUnavailable = ({ message, onReturn }) => (
  <div className="provider-access-state is-error">
    <Icon name="shield" size={24}/>
    <h1>Provider sign-in is not available</h1>
    <p>{message}</p>
    <Button variant="secondary" onClick={onReturn}>Return home</Button>
  </div>
);

export const ProviderAccessWelcome = ({ onSignIn, onPreview }) => (
  <div className="provider-access-page route-in">
    <section className="provider-access-welcome">
      <div>
        <Badge tone="brand" icon="shield">Verified provider access</Badge>
        <h1>Claim your official provider record</h1>
        <p>
          Sign in with a verified email, confirm your phone, and submit the
          Pennsylvania facility you are authorized to manage. Pilot claims are
          reviewed manually before access is granted.
        </p>
        <div className="provider-access-actions">
          <Button size="lg" onClick={onSignIn}>Sign in or create an account</Button>
          <Button size="lg" variant="secondary" onClick={onPreview}>
            Preview provider onboarding
          </Button>
        </div>
      </div>
      <aside className="provider-access-trust">
        <span><Icon name="check-circle" size={17}/>Email verified by Microsoft Entra</span>
        <span><Icon name="phone" size={17}/>Phone verified by one-time code</span>
        <span><Icon name="user" size={17}/>Every facility claim reviewed manually</span>
        <span><Icon name="shield" size={17}/>Official data remains read-only</span>
      </aside>
    </section>
  </div>
);

export const ProviderAccountHeader = ({
  isAdmin,
  onReview,
  onSignOut,
}) => (
  <section className="provider-access-header">
    <div className="container provider-access-header-row">
      <div>
        <Badge tone="brand">Provider account</Badge>
        <h1>Set up verified access</h1>
        <p>
          Complete the steps below. Nothing changes on a provider profile until
          your claim is approved.
        </p>
      </div>
      <div className="provider-access-header-actions">
        {isAdmin && (
          <Button variant="secondary" onClick={onReview}>Review claims</Button>
        )}
        <Button variant="ghost" onClick={onSignOut}>Sign out</Button>
      </div>
    </div>
  </section>
);

export const VerificationSteps = ({ profile }) => (
  <div className="provider-verification-steps">
    <article className="provider-verification-step is-complete">
      <span><Icon name="check" size={15}/></span>
      <div>
        <small>Step 1</small>
        <h2>Email verified</h2>
        <p>{profile.email}</p>
      </div>
    </article>
    <article className={`provider-verification-step ${profile.phoneVerifiedAt ? 'is-complete' : 'is-current'}`}>
      <span>{profile.phoneVerifiedAt ? <Icon name="check" size={15}/> : '2'}</span>
      <div>
        <small>Step 2</small>
        <h2>Phone verification</h2>
        <p>{profile.phoneVerifiedAt ? profile.phoneE164 : 'Required before submitting a claim'}</p>
      </div>
    </article>
    <article className={`provider-verification-step ${profile.phoneVerifiedAt ? 'is-current' : ''}`}>
      <span>3</span>
      <div>
        <small>Step 3</small>
        <h2>Facility claim</h2>
        <p>Select the official record you are authorized to manage</p>
      </div>
    </article>
  </div>
);

export const PhoneVerificationPanel = ({
  phone,
  onPhoneChange,
  challenge,
  code,
  onCodeChange,
  onStart,
  onConfirm,
}) => (
  <section className="provider-access-panel">
    <div className="provider-access-panel-heading">
      <div>
        <span>Secure contact</span>
        <h2>Verify your mobile number</h2>
      </div>
      <Badge tone="neutral">One-time code</Badge>
    </div>
    {!challenge ? (
      <form className="provider-inline-form" onSubmit={onStart}>
        <Field label="Mobile phone" hint="Used for account security and provider updates.">
          <Input
            value={phone}
            onChange={(event) => onPhoneChange(event.target.value)}
            placeholder="(215) 555-0100"
            inputMode="tel"
            required
          />
        </Field>
        <Button type="submit">Send verification code</Button>
      </form>
    ) : (
      <form className="provider-inline-form" onSubmit={onConfirm}>
        <Field label={`Code sent to ${challenge.phoneE164}`}>
          <Input
            value={code}
            onChange={(event) => onCodeChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
          />
        </Field>
        <Button type="submit" disabled={code.length !== 6}>Confirm phone</Button>
      </form>
    )}
  </section>
);

export const ProviderSearchPanel = ({
  search,
  onSearchChange,
  onSearch,
  searching,
  providers,
  selectedProvider,
  onSelect,
  onSubmit,
  submitting,
}) => (
  <section className="provider-access-panel">
    <div className="provider-access-panel-heading">
      <div>
        <span>Official Pennsylvania records</span>
        <h2>Find your facility</h2>
      </div>
      <Badge tone="brand" icon="shield">Read-only source</Badge>
    </div>
    <form className="provider-search-form" onSubmit={onSearch}>
      <Input
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Search by official facility name"
        icon="search"
      />
      <Button type="submit" disabled={search.trim().length < 2 || searching}>
        {searching ? 'Searching…' : 'Search'}
      </Button>
    </form>
    {providers.length > 0 && (
      <div className="provider-claim-results">
        {providers.map((provider) => (
          <button
            type="button"
            key={provider.id}
            className={selectedProvider?.id === provider.id ? 'is-selected' : ''}
            onClick={() => onSelect(provider)}
          >
            <span>
              <strong>{provider.facilityName}</strong>
              <small>
                {provider.address.line1}, {provider.address.city}, {provider.address.zipCode}
              </small>
            </span>
            <span>
              <Badge tone="neutral">{provider.officialDetails.starLevel || 'No STAR level'}</Badge>
              <Icon name={selectedProvider?.id === provider.id ? 'check-circle' : 'chev-right'} size={17}/>
            </span>
          </button>
        ))}
      </div>
    )}
    {selectedProvider && (
      <div className="provider-claim-confirm">
        <div>
          <strong>Submit {selectedProvider.facilityName} for review?</strong>
          <p>You are confirming that you are authorized to manage this facility.</p>
        </div>
        <Button onClick={onSubmit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit claim'}
        </Button>
      </div>
    )}
  </section>
);

export const ClaimsPanel = ({ claims, onManage }) => (
  <section className="provider-access-panel">
    <div className="provider-access-panel-heading">
      <div>
        <span>Claim history</span>
        <h2>Your facility claims</h2>
      </div>
      <Badge tone="neutral">{claims.length} total</Badge>
    </div>
    {claims.length ? (
      <div className="provider-claim-list">
        {claims.map((item) => (
          <article key={item.id}>
            <div>
              <strong>{item.providerName}</strong>
              <small>Official ID {item.providerId}</small>
            </div>
            <Badge tone={statusTone[item.status]}>{item.status}</Badge>
            <p>
              {item.status === 'pending' && 'Waiting for manual pilot review.'}
              {item.status === 'approved' && 'Approved for provider access.'}
              {item.status === 'rejected' && (item.decisionReason || 'The claim was not approved.')}
            </p>
            {item.status === 'approved' && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onManage(item.providerId)}
              >
                Manage availability
              </Button>
            )}
          </article>
        ))}
      </div>
    ) : (
      <div className="provider-claim-empty">
        <Icon name="home" size={22}/>
        <strong>No facility claims yet</strong>
        <span>Search above after your phone is verified.</span>
      </div>
    )}
  </section>
);
